import { X, Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import type { ProcessingStage, UploadResult } from '../hooks/useFailSafeUpload';

interface UploadStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  isUploading: boolean;
  isProcessing: boolean;
  uploadProgress: number;
  processingStages: ProcessingStage[];
  currentError: string | null;
  uploadResult: UploadResult | null;
  fileName: string;
  fileType?: string;
  onRetry?: () => void;
}

const getStageIcon = (status: ProcessingStage['status']) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-400" />;
    case 'in-progress':
      return <Loader className="w-4 h-4 text-blue-400 animate-spin" />;
    case 'error':
      return <AlertCircle className="w-4 h-4 text-red-400" />;
    default:
      return <div className="w-4 h-4 rounded-full border-2 border-white/20" />;
  }
};

const getStageColor = (status: ProcessingStage['status']) => {
  switch (status) {
    case 'completed':
      return 'bg-green-500/10 border-green-500/20';
    case 'in-progress':
      return 'bg-blue-500/10 border-blue-500/20';
    case 'error':
      return 'bg-red-500/10 border-red-500/20';
    default:
      return 'bg-white/5 border-white/10';
  }
};

export default function UploadStatusModal({
  isOpen,
  onClose,
  isUploading,
  isProcessing,
  uploadProgress,
  processingStages,
  currentError,
  uploadResult,
  fileName,
  fileType,
  onRetry,
}: UploadStatusModalProps) {
  if (!isOpen) return null;

  const isActive = isUploading || isProcessing || !!currentError;
  const completedStages = processingStages.filter((s) => s.status === 'completed').length;
  const totalStages = processingStages.length;
  const overallProgress = isUploading ? uploadProgress : Math.round((completedStages / totalStages) * 100);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-gradient-to-br from-indigo-900/80 via-purple-900/80 to-pink-900/80 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm">
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Upload Status</h2>
              <p className="text-sm text-white/60">{fileName}</p>
              {fileType && <p className="text-xs text-white/50 mt-0.5">{fileType}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isActive}
            className="p-1 hover:bg-white/10 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Overall Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white">Uploading file...</p>
                <p className="text-sm font-bold text-white">{uploadProgress}%</p>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Processing Stages */}
          {isProcessing && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white">Processing...</p>
                <p className="text-sm font-bold text-white">{overallProgress}%</p>
              </div>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Stage List */}
          <div className="space-y-2 mt-6">
            {processingStages.map((stage) => (
              <div
                key={stage.id}
                className={`p-3 rounded-lg border transition-all duration-200 ${getStageColor(stage.status)}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">{getStageIcon(stage.status)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{stage.label}</p>
                    <p className="text-xs text-white/70 mt-0.5 line-clamp-2">{stage.message}</p>
                    {stage.errorDetails && (
                      <p className="text-xs text-red-300 mt-1">{stage.errorDetails}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Success Message */}
          {uploadResult?.success && !isProcessing && !isUploading && (
            <div className="p-4 rounded-lg bg-green-500/15 border border-green-500/30 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="font-medium text-green-300">Upload Complete!</p>
              </div>
              <div className="text-sm text-green-200/80 space-y-1">
                <p>✓ {uploadResult.wordCount} words extracted</p>
                <p>✓ {uploadResult.analysis.citations.length} citations identified</p>
                <p>✓ Document type: {uploadResult.analysis.category}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {currentError && (
            <div className="p-4 rounded-lg bg-red-500/15 border border-red-500/30 space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-300">Upload Failed</p>
                  <p className="text-sm text-red-200/80 mt-1">{currentError}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 p-6 bg-white/5 backdrop-blur-sm flex gap-3">
          {!uploadResult?.success && !currentError && (
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Loader className="w-4 h-4 animate-spin" />
              <span>
                {isUploading ? 'Uploading' : isProcessing ? 'Processing' : 'Initializing'}...
              </span>
            </div>
          )}

          {uploadResult && uploadResult.success && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-green-300 font-medium transition-all duration-200"
            >
              Close
            </button>
          )}

          {currentError && (
            <>
              <button
                onClick={onRetry}
                className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-blue-300 font-medium transition-all duration-200"
              >
                Retry
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white/80 font-medium transition-all duration-200"
              >
                Cancel
              </button>
            </>
          )}

          {!uploadResult && !currentError && isActive && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white/80 font-medium transition-all duration-200"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
